<template>
  <div
    class="anz-posts__item"
    :class="{
      'anz-posts__item--nested': nested,
      'anz-posts__item--violation': isHidden(),
      'anz-posts__item--box': boxPost,
    }"
  >
    <template v-if="postEditing">
      <!-- post editing form -->
      <keep-alive>
        <Suspense v-if="postEditing">
          <template #default>
            <post-edit
              :post="postRoot.post"
              :post-editing="postEditing"
              @post-updated="postEditing = false"
              @post-close="postEditing = false"
            />
          </template>
          <template #fallback>
            <a-loader class-name="my-l" />
          </template>
        </Suspense>
      </keep-alive>
    </template>
    <template v-else>
      <div
        class="anz-post"
        :class="{
          'anz-post--mine': isMinePost && highlightMine,
          'anz-post--selected': isSelectedPost,
          'anz-post--lastSaved': isLastSavedPost,
          'anz-post--disabled': isHidden(),
          'anz-post--box': boxPost,
          'anz-post--tooltip-active': currentComponent !== '' || isResponding,
        }"
        data-cy="post-single"
      >
        <div class="anz-post__wrapper">
          <div
            class="anz-post__header anz-flex anz-flex-wrap anz-justify-content-between anz-align-items-center anz-mb-xs"
          >
            <!-- Link to Topic -->
            <template v-if="showLinkToTopic">
              <div
                class="anz-post__header-topic-link anz-text-line-clamp anz-text-line-clamp-1-sm anz-flex-fill anz-pr-l"
              >
                <slot name="topicLinkPrefix"></slot>
                <strong class="anz-color-primary anz-strong anz-mr-xs">{{
                  $translate('post.actions.article')
                }}</strong>
                <a-button
                  add-href
                  :href="generateOriginRoute(postRoot.post.topic)"
                  :title="postRoot.post.topic.topicName"
                  className="anz-link anz-link-hover-underline anz-font-bold anz-mr-s"
                />
              </div>
            </template>

            <post-author
              v-if="!hideUsername"
              :post-root="postRoot"
              :allow-show-new="allowShowNew"
              :show-author-above="showAuthorAbove"
              :is-mine-pos="isMinePost"
              :hide-user-blog="hideUserBlog"
            />
            <post-information :post-root="postRoot" :show-ip="showIp" />
          </div>
          <post-content
            @toggle-edit-form="toggleTooltip"
            :post-root="postRoot"
            :show-content-author="showContentAuthor"
            :strip-long-text-suffix="stripLongTextSuffix"
            :strip-long-text-button="stripLongTextButton"
            :strip-long-text-length="stripLongTextLength"
            :strip-long-text="stripLongText"
            :show-parent-author-ref="showParentAuthorRef"
            :show-parent-annotation="showParentAnnotation"
            :allow-show-new="allowShowNew"
          />
          <!-- Post Footer -->
          <footer
            class="anz-post__footer anz-flex-wrap anz-pb-xs anz-font-default anz-fs-11-xxsm anz-fs-12-xsmo anz-fs-xs anz-fs-xxxs-sm anz-mt-s"
            :class="{
              'anz-post__footer-is-active': footerIsActive,
              'anz-hidden': hideFooter,
              'anz-flex': !hideFooter,
            }"
          >
            <ul class="anz-inline-list-bar">
              <!-- Subscribe button -->
              <li v-if="showSubscribeButton">
                <a-button
                  class-name="anz-link anz-flex anz-align-items-center anz-color-gray-600 anz-link-hover-underline"
                  :class="{ 'anz-link-disabled': postActionsDisabled }"
                  @click="subscribePost"
                  :loading="isSubscribing"
                  :disabled="isSubscribing || postActionsDisabled"
                  data-cy="post-subscribe"
                  showIcon
                  icon-name="bell"
                  icon-width="1.05em"
                  icon-height="1.15em"
                  icon-class="anz-hidden-sm"
                  loader-class="anz-spinner-xxs anz-mr-xs"
                >
                  <template #title>
                    <span class="anz-hidden-mo">{{
                      $translate('post.actions.subscribe')
                    }}</span>
                  </template>
                </a-button>
              </li>
              <!-- Share button -->
              <li v-if="!hideShareButton">
                <a-button
                  class-name="anz-link anz-flex anz-align-items-center anz-color-gray-600 anz-link-hover-underline"
                  @click="copyLink"
                  :class="{ 'anz-link-disabled': postActionsDisabled }"
                  data-cy="post-show-copy-link"
                  show-icon
                  icon-name="share"
                  icon-width="1.15em"
                  icon-height="1.1em"
                  icon-class="anz-hidden-sm"
                  loader-class="anz-spinner-xxs anz-mr-xs"
                >
                  <template #title>
                    <span class="anz-hidden-mo">{{
                      $translate('post.actions.share')
                    }}</span>
                  </template>
                </a-button>
              </li>
              <li v-if="showReportButton">
                <!-- Report button -->
                <a-button
                  class-name="anz-link anz-flex anz-align-items-center anz-color-gray-600 anz-link-hover-underline"
                  :class="{ 'anz-link-disabled': postActionsDisabled }"
                  @click="postReportQuestion"
                  :disabled="postActionsDisabled"
                  data-cy="post-report"
                  showIcon
                  icon-name="megaphone"
                  icon-width="1em"
                  icon-height="0.9285em"
                  icon-class="anz-hidden-sm"
                  loader-class="anz-spinner-xs anz-mr-xs"
                >
                  <template #title>
                    <span class="anz-hidden-mo">{{
                      $translate('post.actions.report')
                    }}</span>
                  </template>
                </a-button>
              </li>
            </ul>

            <!-- Super like, Like and Disslike button -->
            <span
              v-if="!hideVoteButton"
              class="anz-post__footer-likes anz-pl-m anz-pl-l-sm anz-mx-m anz-mx-l-sm anz-align-center anz-ml-auto anz-flex anz-align-items-center"
            >
              <a-button
                class-name="anz-link anz-link-vote anz-link-underline-none anz-align-center anz-flex-inline anz-align-items-center anz-justify-content-center anz-mr-m anz-mr-l-sm anz-link-hover-underline"
                :class="{
                  'anz-link-disabled': postSuperLikeDisabled,
                  'is-active anz-svg-icon-bigger':
                    postRoot.post.stats.superLikes > 0,
                }"
                @click="postSuperLikeQuestion()"
                data-cy="post-vote-super-like"
                showIcon
                icon-name="star"
                icon-width="1.084em"
                icon-height="1.055em"
                iconClass="anz-mr-xxs"
                loader-class="anz-spinner-xs anz-mr-xxs"
                :tooltip-title="
                  $translate('post.tooltip.superLike.confirmationAction')
                "
              >
                <template #title>
                  {{ postRoot.post.stats.superLikes }}
                </template>
              </a-button>
              <a-button
                class-name="anz-link anz-link-underline-none anz-align-center anz-flex-inline anz-align-items-center anz-justify-content-center anz-mr-m anz-mr-l-sm anz-link-hover-underline"
                :class="{
                  'anz-link-disabled': postActionsDisabled,
                  'is-active anz-font-bold anz-svg-icon-bigger': voted,
                }"
                @click="votePositive(postRoot.post)"
                :loading="isVotingPositive"
                :disabled="isVotingPositive || postActionsDisabled"
                data-cy="post-vote-positive"
                showIcon
                icon-name="like"
                icon-width="1.084em"
                icon-height="1.055em"
                iconClass="anz-mr-xxs anz-color-success"
                loader-class="anz-spinner-xs anz-mr-xxs"
              >
                <template #title>
                  {{ postRoot.post.stats.votesPositive }}
                </template>
              </a-button>

              <a-button
                class-name="anz-link anz-link-underline-none anz-align-center anz-flex-inline anz-align-items-center anz-justify-content-center anz-link-hover-underline"
                :class="{
                  'anz-link-disabled': postActionsDisabled,
                  'is-active anz-font-bold anz-svg-icon-bigger': voted,
                }"
                @click="voteNegative(postRoot.post)"
                :loading="isVotingNegative"
                :disabled="isVotingNegative || postActionsDisabled"
                data-cy="post-vote-negative"
                showIcon
                icon-name="dislike"
                icon-width="1.084em"
                icon-height="1.055em"
                iconClass="anz-mr-xxs anz-color-primary-lighter"
                loader-class="anz-spinner-xs anz-mr-xxs"
              >
                <template #title>
                  {{ postRoot.post.stats.votesNegative }}
                </template>
              </a-button>
            </span>
            <!-- Respond buttons -->
            <template v-if="!hideRespondButton">
              <a-button
                v-if="openRespondPage"
                :class="{
                  'anz-link-disabled': openRespondPageDisabled,
                }"
                :title="$translate('post.actions.respond')"
                class-name="anz-link anz-flex anz-align-items-center anz-link-inverse anz-link-hover-underline"
                add-href
                :href="generatePostRespondRoute(postRoot.post)"
                @click="propagatePostRespondEvent"
                data-cy="post-respond"
              >
                <template #title>
                  {{ $translate('post.actions.respond') }}
                </template>
              </a-button>
              <a-button
                v-else
                :title="$translate('post.actions.respond')"
                class-name="anz-link anz-flex anz-align-items-center anz-link-inverse anz-link-hover-underline"
                :class="{
                  'anz-link-disabled': respondDisabled,
                }"
                @click="showResponseForm"
                :disabled="respondDisabled"
                data-cy="post-respond"
              >
                <template #title>
                  {{ $translate('post.actions.respond') }}
                </template>
              </a-button>
            </template>
          </footer>
        </div>
        <!-- Side Button -->
        <div
          class="anz-post__side-btn anz-ml-xxl-sm anz-pl-xxl-sm anz-wf-mo anz-mt-s anz-mt-0-sm anz-flex-mo"
          v-if="boxPost"
        >
          <a-button
            add-href
            :href="generatePostRoute(postRoot.post)"
            class-name="anz-btn anz-btn-border anz-btn-border-hover-primary anz-btn-s-sm anz-wf-mo"
            :title="$translate('post.actions.goToDiscussion')"
          />
          <a-button
            @click="footerIsActive = !footerIsActive"
            :class="{ 'is-active': footerIsActive, 'anz-hidden': hideFooter }"
            class-name="anz-post__btn-settings anz-btn anz-btn-border anz-btn-border-hover-primary anz-btn-s-sm anz-wf-mo anz-ml-s anz-hidden-sm anz-flex-fill anz-px-xs anz-btn-icon anz-color-gray-500"
          >
            <template #title>
              <a-icon
                class="anz-mx-xs"
                name="settings-menu"
                width="1.1428em"
                height="0.3571em"
              />
            </template>
          </a-button>
        </div>
      </div>

      <!-- Show in thread button -->
      <div class="anz-align-center anz-mt-s" v-if="showRedirectToThreadButton">
        <a-button
          :title="$translate('post.actions.showInThread')"
          class-name="anz-btn anz-btn-border anz-btn-s"
          add-href
          :href="generatePostRoute(postRoot.post)"
          data-cy="post-respond"
        >
        </a-button>
      </div>

      <component
        :is="currentComponent"
        :alreadyPerformed="alreadyPerformed"
        :changed="changed"
        :post="postRoot.post"
        @tooltip-close="currentComponent = ''"
      ></component>
      <div v-if="showLoginText" data-cy="post-respond-result">
        <login-banner
          :place-holder="$translate('post.texts.loginBanner')"
          show-login-link
        />
      </div>
      <div v-if="showOnlySubsText" data-cy="post-respond-result">
        <login-banner
          :place-holder="$translate('post.texts.subscribeBanner')"
        ></login-banner>
      </div>
      <div
        class="anz-posts__item-modal anz-posts__item-create-wrapper"
        :class="{ 'is-active': isResponding }"
        v-if="isResponding"
        data-cy="post-create-respond-container"
      >
        <div class="anz-hidden-sm">
          <div
            class="anz-hidden-sm anz-bb anz-mx-m anz-flex anz-justify-content-between anz-align-items-center"
          >
            <div class="anz-fs-xxxs anz-text-uppercase anz-strong ">
              {{ $translate('post.labels.modalTitle') }}
            </div>
            <a-button
              class-name="anz-link anz-color-black-50 anz-ml-s anz-btn-close anz-mrn-m"
              @click="isResponding = !isResponding"
            >
              <template #title>
                <a-icon name="cross-solid" width="12px" height="12px" />
              </template>
            </a-button>
          </div>
          <pre></pre>
          <div class="anz-mx-m anz-mt-m anz-posts__item-modal-quote">
            <span class="anz-color-gray-700">
              @{{ postRoot.post.author.nickname }}:
            </span>
            <span
              ref="modalContent"
              class="anz-text-truncate-content"
              v-html="truncate(postRoot.post.content, 70, '')"
            >
            </span>
            <a-button
              v-if="postRoot.post.content.length >= 70"
              class-name="anz-text-truncate-toggle anz-link anz-color-primary"
              :title="$translate('post.actions.showFullPost')"
              @click="expandTruncate(postRoot.post.content, $refs.modalContent)"
            />
          </div>
        </div>
        <post-create
          :title="$translate('post.texts.addPost')"
          :default-title="false"
          :parent-post="postRoot.post"
          allow-close
          @created-post="createdPost"
          @tooltip-close="isResponding = false"
        ></post-create>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  Ref,
  ref,
  reactive,
  computed,
  onMounted,
  nextTick,
} from 'vue'
import { Post, SettingsStatus } from '@/model/data-object/Post'
import AButton from '@/components/Forms/AButton.vue'
import AIcon from '@/components/AIcon.vue'
import PostCreate from '@/components/Post/PostCreate.vue'
import usePostVote from '@/composables/post-vote/postVote'
import { DateTimeHelper } from '@/helper/dateTime'
import useCurrentUser from '@/composables/user/user'
import useAppState from '@/composables/ui/ui'
import { useRouter } from '@/routing/router'
import { useSubscribePost } from '@/composables/subscribers/subscribersManager'
import LoginBanner from '@/components/Post/LoginBanner.vue'
import PostSingleViolation from '@/components/Post/PostSingleViolation.vue'
import useScrollManager from '@/composables/scroller/useScrollManager'
import PostCopyUrl from '@/components/Post/Tooltips/PostCopyUrl.vue'
import PostVoteTooltip from '@/components/Post/Tooltips/PostVoteTooltip.vue'
import PostSuperLikeTooltip from '@/components/Post/Tooltips/PostSuperLikeTooltip.vue'
import PostSubscribeTooltip from '@/components/Post/Tooltips/PostSubscribeTooltip.vue'
import PostSuperLikeLoginTooltip from '@/components/Post/Tooltips/PostSuperLikeLoginTooltip.vue'
import PostSuperLikeQuotaTooltip from '@/components/Post/Tooltips/PostSuperLikeQuotaTooltip.vue'
import PostSuperLikeUpgradeTooltip from '@/components/Post/Tooltips/PostSuperLikeUpgradeTooltip.vue'
import PostReportConfirmationTooltip from '@/components/Post/Tooltips/PostReportConfirmationTooltip.vue'
import PostEditTooltip from '@/components/Post/Tooltips/PostEditTooltip.vue'
import PostSuperLikeBuySubscriptionTooltip from '@/components/Post/Tooltips/PostSuperLikeBuySubscriptionTooltip.vue'
import PostBannedTooltip from '@/components/Post/Tooltips/PostBannedTooltip.vue'
import PostVoteLoginTooltip from '@/components/Post/Tooltips/PostVoteLoginTooltip.vue'
import PostSubscribeLoginTooltip from '@/components/Post/Tooltips/PostSubscribeLoginTooltip.vue'
import {
  TOOLTIP_COMPONENTS,
  usePostTooltip,
} from '@/composables/post/postTooltip'
import useAnalyticsManager, {
  ANALYTICS_EVENT_POST_COPY_LINK,
  ANALYTICS_EVENT_POST_REPORT,
  ANALYTICS_EVENT_POST_RESPOND,
} from '@/composables/analytics/analyticsManager'
import useConfiguration from '@/composables/configuration/configuration'
import useUiFactory from '@/composables/ui/uiFactory'
import PostRespondBanned from '@/components/Post/PostRespondBanned.vue'
import usePostSingleUiActions from '@/composables/ui/uiActions'
import AError from '@/components/Post/SinglePost/AError.vue'
import PostSingleParent from '@/components/Profile/PostSingleParent.vue'
import PostAuthor from '@/components/Post/SinglePost/PostAuthor.vue'
import PostInformation from '@/components/Post/SinglePost/PostInformation.vue'
import usePostCreate from '@/composables/post/postCreate'
import PostEdit from '@/components/Post/SinglePost/PostEdit.vue'
import ALoader from '@/components/ALoader.vue'
import { usePostSuperLikeTooltip } from '@/composables/post-super-like/postSuperLike'
import { usePostEditTooltip } from '@/composables/post/postEdit'
import PostContent from '@/components/Post/SinglePost/PostContent.vue'

export default defineComponent({
  name: 'PostSingle',
  emits: ['post-responded'],
  components: {
    PostContent,
    ALoader,
    PostEdit,
    PostInformation,
    PostSuperLikeUpgradeTooltip,
    PostEditTooltip,
    PostSuperLikeLoginTooltip,
    PostSuperLikeQuotaTooltip,
    PostSubscribeLoginTooltip,
    PostSuperLikeBuySubscriptionTooltip,
    PostAuthor,
    PostSingleParent,
    AError,
    PostRespondBanned,
    PostVoteTooltip,
    PostSuperLikeTooltip,
    PostCopyUrl,
    PostSingleViolation,
    LoginBanner,
    AButton,
    AIcon,
    PostSubscribeTooltip,
    PostReportConfirmationTooltip,
    PostCreate,
    PostBannedTooltip,
    PostVoteLoginTooltip,
  },
  data() {
    return {
      footerIsActive: false,
      editing: false,
    }
  },
  props: {
    postRoot: {
      type: Object, // todo change type to post
      default: {},
    },
    nested: {
      type: Boolean,
      default: false,
    },
    boxPost: {
      type: Boolean,
      default: false,
    },
    showReportButton: {
      type: Boolean,
      default: false,
    },
    hideShareButton: {
      type: Boolean,
      default: false,
    },
    hideRespondButton: {
      type: Boolean,
      default: false,
    },
    hideVoteButton: {
      type: Boolean,
      default: false,
    },
    stripLongText: {
      type: Boolean,
      default: false,
    },
    stripLongTextLength: {
      type: Number,
      default: 235,
    },
    stripLongTextSuffix: {
      type: String,
      default: ' ',
    },
    stripLongTextButton: {
      type: Boolean,
      default: true,
    },
    showSubscribeButton: {
      type: Boolean,
      default: false,
    },
    showRedirectToThreadButton: {
      type: Boolean,
      default: false,
    },
    showArticleLink: {
      type: Boolean,
      default: false,
    },
    allowShowNew: {
      type: Boolean,
      default: false,
    },
    highlightMine: {
      type: Boolean,
      default: false,
    },
    showIp: {
      type: Boolean,
      default: false,
    },
    hideUsername: {
      type: Boolean,
      default: false,
    },
    hideUserBlog: {
      type: Boolean,
      default: false,
    },
    openRespondPage: {
      type: Boolean,
      default: false,
    },
    showLinkToTopic: {
      type: Boolean,
      default: false,
    },
    allowAuthorSeenBadge: {
      type: Boolean,
      default: false,
    },
    showAuthorAbove: {
      type: Boolean,
      default: false,
    },
    showContentAuthor: {
      type: String,
      default: '',
    },
    hideFooter: {
      type: Boolean,
      default: false,
    },
    showParentAnnotation: {
      type: Boolean,
      default: false,
    },
    showParentAuthorRef: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    dateTimeFormatted() {
      return DateTimeHelper.formatDateTime(this.postRoot.post.createdAt)
    },
    isHidden() {
      return [SettingsStatus.HIDDEN, SettingsStatus.GDPR_DELETED].includes(
        this.postRoot.post.status
      )
    },
    expandTruncate(text: any, target: any) {
      if (target === undefined) {
        return false
      }

      target.innerHTML = text
      target.classList.add('is-expanded')
    },
    truncate(text: any, length: number, suffix: any) {
      if (text.length > length) {
        return text.substring(0, length) + suffix
      } else {
        return text
      }
    },
  },
  setup(props, context) {
    const { currentUser, allowPayingCustomerAction } = useCurrentUser()
    const {
      postIdToShow,
      loggedIn,
      lastSavedPost,
      firstForumVisit,
    } = useAppState()
    const newPost = reactive<Post | object>({})
    const isResponding: Ref<boolean> = ref(false)
    const showLoginText: Ref<boolean> = ref(false)
    const showOnlySubsText: Ref<boolean> = ref(false)
    const { respondToPostId } = useAppState()
    const { postRespond } = usePostCreate()
    const { alreadyPerformed, currentComponent, changed } = usePostTooltip()

    const isSelectedPost = computed(() => {
      return postIdToShow.value === props.postRoot.post.id
    })

    const isLastSavedPost = computed(() => {
      return (
        lastSavedPost.value && lastSavedPost.value.id === props.postRoot.post.id
      )
    })

    function createdPost(post: Post) {
      isResponding.value = false
      context.emit('post-responded', post)
      postRespond(post)
    }

    const propagatePostRespondEvent = () => {
      const { propagateAnalyticsEvent } = useAnalyticsManager()
      propagateAnalyticsEvent(
        props.postRoot.post as Post,
        ANALYTICS_EVENT_POST_RESPOND
      )
    }

    const redirectToShowRespondForm = () => {
      propagatePostRespondEvent()
      const { reloadHash } = useUiFactory()
      reloadHash(['respondToPostId'])
    }

    const showResponseForm = () => {
      if (!isResponding.value) {
        propagatePostRespondEvent()
      }
      if (allowPayingCustomerAction.value) {
        if (currentUser.value?.isBanned) {
          currentComponent.value = TOOLTIP_COMPONENTS.POST_BANNED

          return
        }

        isResponding.value = !isResponding.value

        return
      }
      if (loggedIn.value) {
        showOnlySubsText.value = !showOnlySubsText.value

        return
      }

      showLoginText.value = !showLoginText.value
    }

    if (respondToPostId.value === props.postRoot.post.id) {
      showResponseForm()
    }

    onMounted(() => {
      nextTick(() => {
        props.postRoot.mounted = true

        if (isLastSavedPost.value && !isSelectedPost.value) {
          const { scrollToLastSaved } = useScrollManager()
          scrollToLastSaved()
        }
      })
    })

    const copyLink = () => {
      if (currentComponent.value !== TOOLTIP_COMPONENTS.POST_COPY) {
        const { propagateAnalyticsEvent } = useAnalyticsManager()
        propagateAnalyticsEvent(
          props.postRoot.post as Post,
          ANALYTICS_EVENT_POST_COPY_LINK
        )
        currentComponent.value = TOOLTIP_COMPONENTS.POST_COPY

        return
      }

      currentComponent.value = undefined
    }

    const { configuration } = useConfiguration()

    const postLinkDisabled = () => {
      return configuration.widget.postDateLinkDisabled
    }
    const postReportQuestion = () => {
      if (
        currentComponent.value === TOOLTIP_COMPONENTS.POST_REPORT_CONFIRMATION
      ) {
        currentComponent.value = undefined

        return
      }
      const { propagateAnalyticsEvent } = useAnalyticsManager()
      propagateAnalyticsEvent(
        props.postRoot.post as Post,
        ANALYTICS_EVENT_POST_REPORT
      )
      currentComponent.value = TOOLTIP_COMPONENTS.POST_REPORT_CONFIRMATION
    }

    const postUiActions = usePostSingleUiActions(props.postRoot.post as Post)

    return {
      configuration,
      newPost,
      postIdToShow,
      isResponding,
      firstForumVisit,
      isSelectedPost,
      showLoginText,
      showOnlySubsText,
      loggedIn,
      currentComponent,
      alreadyPerformed,
      changed,
      isLastSavedPost,
      postLinkDisabled,
      copyLink,
      createdPost,
      propagatePostRespondEvent,
      redirectToShowRespondForm,
      showResponseForm,
      postReportQuestion,
      ...useSubscribePost(
        props.postRoot.post as Post,
        currentComponent,
        alreadyPerformed
      ),
      ...usePostSuperLikeTooltip(props.postRoot.post as Post, currentComponent),
      ...useRouter(),
      ...postUiActions,
      ...usePostVote(currentComponent, alreadyPerformed, changed),
      ...usePostEditTooltip(currentComponent, postUiActions.postEditing),
    }
  },
})
</script>
